import {Component, EventEmitter, Input, Output, inject} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators, ReactiveFormsModule} from '@angular/forms';
import {dateValidator, enterOneOf, phoneValidator, sinValidator} from '../../../../_utils/validators';
import {clearErrors, valueExists} from '../../../../_utils/forms';
import {StandardSubjectFormComponent} from '../standard-subject-form.component';
import {ACCOUNT_NUMBER_PATTERN, ADDRESS_PATTERN, EMPLOYER_PATTERN, IP_PATTERN, POSTAL_PATTERN} from '../../../../_constants/constants';
import {i18nKeyMap} from '../../../../_utils/lang';
import {I18nService} from '../../../../_services/gui/i18n.service';
import {toUpper} from 'lodash';
import {NgClass, KeyValuePipe} from '@angular/common';
import {HideOnBlurDirective} from '../../../../_directives/hide-on-blur.directive';
import {NgxMaskModule} from 'ngx-mask';
import {ValidationErrorsComponent as ValidationErrorsComponent_1} from '../../../../common/forms/validation-errors/validation-errors.component';
import {TranslateModule} from '@ngx-translate/core';

export let idxProductTypes: Map<string, string>;
export let idxInteractionTypes: Map<string, string>;
export let idxApplicationChannels: Map<string, string>;

/**
 * This component is the primary component for the Subject form in a consumer request.  Eg: when it's an individual/joint request and subject data has to be entered.
 *
 * The flow of form data as it works its way up from child to parent component can be explained like this:
 *  <ol>
 *    <li>
 *      The <form /> element in the {@link StandardSubjectFormComponent} has its onSubmit configured (via {@link SubjectFormBaseComponent}) to emit the form data
 *      through its output param
 *    </li>
 *    <li>
 *      That output param is configured in the parent component (this component) to echo up through its own output param - it's passing through to the grandparent component.
 *      In this case, the {@link ConsumerServicesComponent}
 *    </li>
 *  </ol>
 *
 * Here is an ascii sequence diagram (generated by PlantUML):
 * <pre>
 *
 *                                         ,------------------------.                                                        ,---------------------------.
 * ,----------------------------.          │SubjectFormBaseComponent│          ,----------------------------.                │ConsumerServicesComponent  │
 * │StandardSubjectFormComponent│          │.onSubmit()             │          │ConsumerSubjectFormComponent│                │.submitPrimarySubjectInfo()│
 * `-------------+--------------'          `-----------+------------'          `-------------+--------------'                `-------------+-------------'
 *               │            form Submit              │                                     │                                             │
 *               │ ----------------------------------->│                                     │                                             │
 *               │                                     │                                     │                                             │
 *               │                                     │       emit subjectFormGroup         │                                             │
 *               │                                     │       through (submittedData)       │                                             │
 *               │                                     │ ----------------------------------->│                                             │
 *               │                                     │                                     │                                             │
 *               │                                     │                                     │ relay submitted data up via passthrough     │
 *               │                                     │                                     │ (submittedData)="submittedData.emit($event)"│
 *               │                                     │                                     │ ------------------------------------------->│
 * ,-------------+--------------.          ,-----------+------------.          ,-------------+--------------.                ,-------------+-------------.
 * │StandardSubjectFormComponent│          │SubjectFormBaseComponent│          │ConsumerSubjectFormComponent│                │ConsumerServicesComponent  │
 * `----------------------------'          │.onSubmit()             │          `----------------------------'                │.submitPrimarySubjectInfo()│
 *                                         `------------------------'                                                        `---------------------------'
 * </pre>
 *
 * @see {@link StandardSubjectFormComponent}
 */
@Component({
    selector: 'app-consumer-subject-form',
    templateUrl: './consumer-subject-form.component.html',
    styleUrls: ['./consumer-subject-form.component.scss'],
    imports: [StandardSubjectFormComponent, ReactiveFormsModule, NgClass, HideOnBlurDirective, NgxMaskModule, ValidationErrorsComponent_1, KeyValuePipe, TranslateModule]
})
export class ConsumerSubjectFormComponent {

  @Output() submittedData: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();
  @Output() back: EventEmitter<void> = new EventEmitter<void>();

  subjectFormGroup: UntypedFormGroup;
  identificationFormGroup: UntypedFormGroup;
  addressFormGroup: UntypedFormGroup;
  prevAddressFormGroup: UntypedFormGroup;
  idxFormGroup: UntypedFormGroup;

  //This is here because it's in the legacy site.  But it doesn't actually do anything.
  //It gets sent to the backend in the creditCard type attribute, but that's it.
  cardTypes: string[] = ['', 'Master Card(1)', 'Visa(2)', 'CIBC Amex(3)'];

  /**
   * This gets passed to the nested validation-error components.
   *
   * @see {@link ValidationErrorsComponent.displayErrors}
   */
  displayErrors: boolean;

  private _isIdx: boolean = false;

  private formBuilder = inject(UntypedFormBuilder);
  private i18nService = inject(I18nService);

  constructor() {
    const formBuilder = this.formBuilder;


    this.subjectFormGroup = ConsumerSubjectFormComponent.buildConsumerSubjectFormGroup(formBuilder);

    this.identificationFormGroup = this.formBuilder.group({
      sin:   ['', sinValidator],
      dob:   ['', dateValidator],
      card1: this.buildCardFormGroup(),
      card2: this.buildCardFormGroup()
    }, {validators: enterOneOf(['sin', 'dob', 'card1', 'card2'])});

    const addressControlsUpdate = {
      streetNum:    ['', Validators.compose([Validators.maxLength(10), Validators.pattern(ADDRESS_PATTERN)])],
      street:       ['', Validators.compose([Validators.required, Validators.maxLength(48), Validators.pattern(ADDRESS_PATTERN)])],
      apartmentNum: ['', Validators.compose([Validators.maxLength(5), Validators.pattern(ADDRESS_PATTERN)])],
      city:         ['', Validators.compose([Validators.required, Validators.maxLength(33), Validators.pattern(ADDRESS_PATTERN)])],
      postalCode:   ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(6), Validators.pattern(POSTAL_PATTERN)])]
    };

    this.addressFormGroup = StandardSubjectFormComponent.buildAddressFormGroup(this.formBuilder, addressControlsUpdate);
    this.prevAddressFormGroup = StandardSubjectFormComponent.buildAddressFormGroup(this.formBuilder, addressControlsUpdate);

    // Stuff for IDX is enabled conditionally when needed, via the enableIdx() method that's called in an Input() setter
  }

  static getSubjectValidators(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const inclPrevAddress = group.get('inclPrevAddress');
      if(!inclPrevAddress?.value) {
        clearErrors(group.get('prevAddress'));
      }
      return;
    };
  }

  static buildConsumerSubjectFormGroup(formBuilder: UntypedFormBuilder): UntypedFormGroup {
    return StandardSubjectFormComponent.buildSubjectFormGroup(formBuilder, {
      firstName:       ['', Validators.compose([Validators.required, Validators.maxLength(20)])],
      middleName:      ['', Validators.maxLength(11)],
      lastName:        ['', Validators.compose([Validators.required, Validators.maxLength(30)])],
      inclPrevAddress: '',
      phone:           ['', Validators.compose([Validators.maxLength(10), phoneValidator])],
      job:             ['', Validators.compose([Validators.maxLength(30), Validators.pattern(ADDRESS_PATTERN)])],
      employer:        ['', Validators.compose([Validators.maxLength(35), Validators.pattern(EMPLOYER_PATTERN)])]
    }, {validators: ConsumerSubjectFormComponent.getSubjectValidators()});
  }

  private buildCardFormGroup(): UntypedFormGroup {
    return this.formBuilder.group({
      type: [''],
      num:  ['', Validators.pattern(ACCOUNT_NUMBER_PATTERN)]
    }, {validators: this.getCardValidators()});
  }

  private getCardValidators(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const type = group.get('type');
      const num = group.get('num');
      if(valueExists(type) && !valueExists(num)) {
        num.setErrors({required: true});
      } else if(!valueExists(type) && valueExists(num)) {
        type.setErrors({required: true});
      } else {
        clearErrors(type);
      }
      return;
    };
  }

  private enableIdx() {
    this.idxFormGroup = this.formBuilder.group({
      clientId:           ['', Validators.compose([Validators.required, Validators.maxLength(10)])],
      emailAddress:       ['', Validators.compose([Validators.required, Validators.email])],
      ipAddress:          ['', Validators.compose([Validators.required, Validators.pattern(RegExp(IP_PATTERN))])],
      productType:        ['', Validators.required],
      interactionType:    ['', Validators.required],
      applicationChannel: ['', Validators.required]
    });

    this.i18nService.getTranslationMap(i18nKeyMap('content.keyed.idx.product-type.', ['card', 'pda', 'telc', 'auto', 'loan', 'loc', 'mort', 'ins'], toUpper))
      .subscribe(m => idxProductTypes = m);

    this.i18nService.getTranslationMap(i18nKeyMap('content.keyed.idx.interaction-type.', ['ao', 'al', 'ac', 'dp', 'ec', 'mi'], toUpper))
      .subscribe(m => idxInteractionTypes = m);

    this.i18nService.getTranslationMap(i18nKeyMap('content.keyed.idx.application-channel.', ['f2f', 'web'], toUpper))
      .subscribe(m => idxApplicationChannels = m);
  }

  public get isIdx(): boolean {
    return this._isIdx;
  }

  @Input()
  public set isIdx(value: boolean) {
    // Setting isIdx explicitly to false according to requirement for removing IDX
    this._isIdx = false;
    if(this._isIdx) {
      this.enableIdx();
    } else {
      // Remove the formGroup if the branch has been set to a non-idx one
      this.idxFormGroup = null;
    }
  }

  public get card1F(): UntypedFormGroup {
    return this.identificationFormGroup.get('card1') as UntypedFormGroup;
  }

  public get card2F(): UntypedFormGroup {
    return this.identificationFormGroup.get('card2') as UntypedFormGroup;
  }

  public get idxProductTypes() {
    return idxProductTypes;
  }

  public get idxInteractionTypes() {
    return idxInteractionTypes;
  }

  public get idxApplicationChannels() {
    return idxApplicationChannels;
  }

}
